/**
 * Slice 4 — optional Maintenance issue (note + photo) or Skip.
 */
import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { toast } from "@/hooks/use-toast";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import { readSimpleFlowState } from "@/lib/simpleTurnover";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";

interface Props {
  propertyId: string;
  taskId: string;
  cleanerId: string;
  entryWalkthroughState: unknown;
  checklistName?: string | null;
  onComplete: () => void;
}

function ProgressSeg({ active }: { active: number }) {
  return (
    <div className="flex gap-1.5">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className={cn("h-[7px] flex-1 rounded-full", i <= active ? "bg-[#24a89a]" : "bg-[#e6edf1]")}
        />
      ))}
    </div>
  );
}

export function SimpleMaintenanceStep({
  propertyId,
  taskId,
  cleanerId,
  entryWalkthroughState,
  checklistName,
  onComplete,
}: Props) {
  const createReport = useCreateMaintenanceReport();
  const fileRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const hasContent = note.trim().length > 0 || !!photoUrl;

  const markDone = async (skipped: boolean) => {
    const prev = readSimpleFlowState(entryWalkthroughState);
    const { error } = await supabase
      .from("cleaning_tasks")
      .update({
        entry_walkthrough_state: {
          ...prev,
          simple_flow: true,
          maintenance_complete: !skipped,
          maintenance_skipped: skipped,
        },
      })
      .eq("id", taskId);
    if (error) throw error;
  };

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const prepared = await prepareImageFileForUpload(file);
      const path = `${propertyId}/${taskId}/maintenance/${crypto.randomUUID()}.jpg`;
      const { error } = await supabase.storage.from("cleaning-photos").upload(path, prepared, {
        contentType: "image/jpeg",
        upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
      setPhotoUrl(data.publicUrl);
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    setBusy(true);
    try {
      await markDone(true);
      onComplete();
    } catch (err) {
      toast({
        title: "Could not continue",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!hasContent) {
      toast({ title: "Add a short note", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await createReport.mutateAsync({
        property_id: propertyId,
        cleaning_task_id: taskId,
        task_description: "Unit maintenance",
        room_name: "General",
        checklist_name: checklistName || null,
        cleaner_id: cleanerId,
        response: "needs_repair",
        notes: note.trim() || null,
        photos: photoUrl ? [photoUrl] : [],
      });
      await markDone(false);
      onComplete();
    } catch (err) {
      toast({
        title: "Could not submit",
        description: err instanceof Error ? err.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] flex-col bg-white">
      <div className="flex flex-col gap-3.5 px-5 pb-3.5 pt-1">
        <div className="text-[30px] font-bold tracking-[-0.6px] text-[#0f1720]">Maintenance</div>
        <ProgressSeg active={simpleFlowSegmentIndex("maintenance")} />
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void upload(f);
        }}
      />

      <div className="flex flex-1 flex-col gap-3 px-5">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a short note"
          className={cn(
            "min-h-[108px] resize-none rounded-[18px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-[18px] py-4 text-lg leading-snug outline-none focus:border-[#24a89a]",
            note.trim() ? "font-bold text-[#0f1720]" : "font-medium text-[#8494a2]",
          )}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="flex h-[100px] flex-col items-center justify-center gap-2 rounded-[18px] border-2 border-dashed border-[#24a89a] bg-[#e2eeed] hover:bg-[#d6e8e6]"
        >
          {busy && !photoUrl ? (
            <Loader2 className="h-6 w-6 animate-spin text-[#15776d]" />
          ) : (
            <>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#15776d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 8.5A2.5 2.5 0 016.5 6h1.2l1.1-2h6.4l1.1 2h1.2A2.5 2.5 0 0120 8.5v8A2.5 2.5 0 0117.5 19h-11A2.5 2.5 0 014 16.5z" />
                <circle cx="12" cy="12.5" r="3.4" />
              </svg>
              <div className="text-[19px] font-bold text-[#15776d]">Add photo</div>
            </>
          )}
        </button>
        {photoUrl && (
          <div className="grid grid-cols-3 gap-2">
            <div className="aspect-square overflow-hidden rounded-[13px] border-[1.5px] border-[#e6edf1]">
              <img src={photoUrl} alt="" className="h-full w-full object-cover" />
            </div>
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-2.5 px-5 pb-6 pt-3.5">
        <button
          type="button"
          disabled={busy || !hasContent}
          onClick={() => void handleSubmit()}
          className={cn(
            "flex h-[68px] items-center justify-center rounded-[18px] text-[22px] font-bold",
            hasContent ? "bg-[#24a89a] text-white hover:bg-[#1f958a]" : "bg-[#dde4ea] text-[#8494a2]",
          )}
        >
          {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : "Submit"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void handleSkip()}
          className="text-center text-[17px] font-medium text-[#64748b] hover:text-[#24a89a]"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
